// Frequency / Tuning preferences — saved per-user.
//
// Shape:
//   {
//     preset: 'original' | 'sabbath_rest' | 'study_focus' |
//             'prayerful_reflection' | 'deep_sleep' | 'custom',
//     a4_hz: number (400..480, default 440),  // tuning reference
//     tone_hz: number (0..2000, 0 means no tone layer),
//     tone_volume: number (0..0.2, default 0.06),
//     crossfade_seconds: number (0..12, default 3),  // Phase B.1
//   }

import { api } from "./api";

const LS_KEY = "yahweh_listening_prefs_v1";

// Allowed listener-facing crossfade options. The backend accepts any value
// in [0, 12]; the UI only ever surfaces these four to keep choices simple.
export const CROSSFADE_OPTIONS = [0, 3, 5, 8];
// Skip / Next button uses a deliberately shorter fade so manual skips stay
// snappy. Capped against the configured crossfade so a 0s setting still
// hard-cuts on Skip.
export const SKIP_FADE_SECONDS = 1;

export const DEFAULT_PREFS = {
  preset: "original",
  a4_hz: 440,
  tone_hz: 0,
  tone_volume: 0.06,
  crossfade_seconds: 3,
};

// Each Believer preset defines its own audio config. "Custom" is freeform.
//
// The solfeggio presets actually PITCH-SHIFT the music (via A4 tuning) so
// the solfeggio frequency becomes the natural anchor for a standard note —
// e.g. 528 Hz preset shifts A4 to 444 so C5 lands on 528. This matches the
// behaviour of dedicated converters (e.g. 432hzconverter.com) and gives a
// real audible warmth/coolness change without a "ringing tone" layered on
// top of the music. Tone layer stays OFF by default — users can opt in
// from Custom if they want a meditation tone alongside.
//
// A4 equivalents are derived from 12-TET. Each one anchors a different
// "natural" note to its solfeggio Hz:
//   174 Hz @ A4=438.48  → F3
//   285 Hz @ A4=452.37  → C#4
//   396 Hz @ A4=444.34  → G4
//   417 Hz @ A4=441.78  → G#4
//   432 Hz @ A4=432.00  → A4 (Sabbath)
//   528 Hz @ A4=444.00  → C5
//   639 Hz @ A4=451.84  → D#5
//   741 Hz @ A4=440.60  → F#5
//   852 Hz @ A4=451.33  → G#5
//   963 Hz @ A4=428.96  → B5
export const PRESETS = {
  original:             { label: "Original Mix",         a4_hz: 440,    tone_hz: 0, tone_volume: 0, blurb: "Default playback. No pitch shift, no tone layer." },
  sabbath_rest:         { label: "Sabbath Rest",         a4_hz: 432,    tone_hz: 0, tone_volume: 0, blurb: "A4 = 432 Hz — warmer, calmer tuning. Music pitched to a cosmic anchor." },
  renewal:              { label: "Renewal",              a4_hz: 452.37, tone_hz: 0, tone_volume: 0, blurb: "Pitched so C♯4 = 285 Hz — restoration & refreshment." },
  release:              { label: "Release",              a4_hz: 444.34, tone_hz: 0, tone_volume: 0, blurb: "Pitched so G4 = 396 Hz — letting go of fear & guilt." },
  transformation:       { label: "Transformation",       a4_hz: 441.78, tone_hz: 0, tone_volume: 0, blurb: "Pitched so G♯4 = 417 Hz — facilitating change & momentum." },
  prayerful_reflection: { label: "Prayerful Reflection", a4_hz: 444,    tone_hz: 0, tone_volume: 0, blurb: "Pitched so C5 = 528 Hz — love & prayerful stillness." },
  unity:                { label: "Unity",                a4_hz: 451.84, tone_hz: 0, tone_volume: 0, blurb: "Pitched so D♯5 = 639 Hz — connection & brotherly love." },
  awakening:            { label: "Awakening",            a4_hz: 440.60, tone_hz: 0, tone_volume: 0, blurb: "Pitched so F♯5 = 741 Hz — intuition & clarity." },
  study_focus:          { label: "Study & Focus",        a4_hz: 451.33, tone_hz: 0, tone_volume: 0, blurb: "Pitched so G♯5 = 852 Hz — airy concentration." },
  divine_light:         { label: "Sacred Light",         a4_hz: 428.96, tone_hz: 0, tone_volume: 0, blurb: "Pitched so B5 = 963 Hz — consciousness & openness." },
  deep_sleep:           { label: "Deep Sleep",           a4_hz: 438.48, tone_hz: 0, tone_volume: 0, blurb: "Pitched so F3 = 174 Hz — deep grounding for sleep." },
  custom:               { label: "Custom",               a4_hz: 440,    tone_hz: 0, tone_volume: 0.06, blurb: "Pick your own A4 tuning and (optional) meditation tone." },
};

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p !== "object") return null;
    return { ...DEFAULT_PREFS, ...p };
  } catch {
    return null;
  }
}

function writeLocal(p) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch {}
}

function isAuthed() {
  return !!localStorage.getItem("yahweh_token");
}

// Exported helper so UI can show "Saved to your profile" feedback only for
// signed-in users (anonymous prefs only persist to localStorage).
export const isSignedInForPrefs = isAuthed;

/** Cents difference from 440 Hz for any given A4. */
export function centsFromA4(a4Hz) {
  if (!Number.isFinite(a4Hz) || a4Hz <= 0) return 0;
  return 1200 * Math.log2(a4Hz / 440);
}

/** Playback rate multiplier so pitch-bound media element plays at the chosen A4. */
export function playbackRateFromA4(a4Hz) {
  if (!Number.isFinite(a4Hz) || a4Hz <= 0) return 1;
  return a4Hz / 440;
}

export function clampPrefs(p) {
  const out = { ...DEFAULT_PREFS, ...p };
  if (!PRESETS[out.preset]) out.preset = "original";
  out.a4_hz = Math.max(400, Math.min(480, Number(out.a4_hz) || 440));
  out.tone_hz = Math.max(0, Math.min(2000, Number(out.tone_hz) || 0));
  out.tone_volume = Math.max(0, Math.min(0.2, Number(out.tone_volume) || 0));
  out.crossfade_seconds = Math.max(0, Math.min(12, Number(out.crossfade_seconds) || 0));
  // Auto-heal stale solfeggio prefs from the pre-2026-02-25 build (which
  // layered an annoying sine tone instead of pitch-shifting). If the saved
  // `preset` is a non-custom key BUT the audio fields don't match the
  // current preset definition, snap them back to the preset's canonical
  // values. Custom is never auto-healed.
  if (out.preset !== "custom") {
    const def = PRESETS[out.preset];
    if (def) {
      out.a4_hz = def.a4_hz;
      out.tone_hz = def.tone_hz;
      out.tone_volume = def.tone_volume;
    }
  }
  return out;
}

export function presetToPrefs(presetKey) {
  const p = PRESETS[presetKey] || PRESETS.original;
  // Crossfade is independent of the preset — preserve whatever the listener
  // already chose when they switch presets.
  const existing = (readLocal() || DEFAULT_PREFS).crossfade_seconds;
  return clampPrefs({
    preset: presetKey,
    a4_hz: p.a4_hz,
    tone_hz: p.tone_hz,
    tone_volume: p.tone_volume,
    crossfade_seconds: existing,
  });
}

export async function loadPrefs() {
  if (isAuthed()) {
    try {
      const { data } = await api.get("/me/listening-prefs");
      if (data?.prefs) {
        const p = clampPrefs(data.prefs);
        writeLocal(p);
        return p;
      }
    } catch { /* fall through to local */ }
  }
  return readLocal() || { ...DEFAULT_PREFS };
}

export function savePrefs(prefs) {
  const p = clampPrefs(prefs);
  writeLocal(p);
  if (isAuthed()) {
    api.put("/me/listening-prefs", p).catch(() => {});
  }
}
